import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import { logout } from "@/modules/auth/store/authSlice";
import { broadcastLogout } from "@/lib/session-sync";
import { HttpStatusCodes } from "@/lib/http-status-codes";
import {
  InsufficientFundsError,
  isInsufficientFundsPayload,
  showInsufficientFundsToast,
} from "@/lib/insufficient-funds";
import { toast } from "sonner";

export interface ApiResult<T> {
  isSuccess: boolean
  value: T
  error?: string
  errorCode?: string
}

interface AuthStoreState {
  organizations?: { currentOrganizationId: string | null };
}

interface AppStore {
  getState: () => AuthStoreState;
  dispatch: ThunkDispatch<AuthStoreState, unknown, UnknownAction>;
}

let store: AppStore | null = null;

export const injectStore = (_store: AppStore) => {
  store = _store;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL || undefined,
  withCredentials: true,
});

// --- REFRESH TOKEN LOGIC ---
let refreshTokenPromise: Promise<boolean> | null = null;

const refreshAuthToken = async (): Promise<boolean> => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      return res.status === HttpStatusCodes.OK;
    } catch {
      broadcastLogout();
      store?.dispatch(logout());
      return false;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

// --- REQUEST INTERCEPTOR ---
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!store) return config;

    const currentOrganizationId = store.getState().organizations?.currentOrganizationId;
    if (currentOrganizationId) {
      config.headers = config.headers ?? {};
      config.headers["X-Organization-Id"] = currentOrganizationId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// --- RESPONSE INTERCEPTOR ---
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!store) return Promise.reject(error);

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if (status === HttpStatusCodes.TOO_MANY_REQUESTS) {
      toast.error("Too many requests. Please try again later.");
      return Promise.reject(error);
    }

    if (status === HttpStatusCodes.PAYMENT_REQUIRED) {
      const payload = error.response?.data;
      if (isInsufficientFundsPayload(payload)) {
        showInsufficientFundsToast(payload.message);
        return Promise.reject(new InsufficientFundsError(payload));
      }
    }

    if (status !== HttpStatusCodes.UNAUTHORIZED || originalRequest._retry) {
      if (originalRequest._retry) {
        broadcastLogout();
        store.dispatch(logout());
      }
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes("/auth/refresh")) {
      broadcastLogout();
      store.dispatch(logout());
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const refreshed = await refreshAuthToken();
      if (refreshed) {
        return apiClient(originalRequest);
      }
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }

    return Promise.reject(error);
  }
);
